import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const ManageTripPermissionsModal = ({ trip, currentUser, onClose, onUpdate }) => {
  const [orgUsers, setOrgUsers] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [isConfidential, setIsConfidential] = useState(trip.is_confidential || false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [trip.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Load organization users
      const usersResponse = await axios.get(`${apiBase}/users`, { headers });
      setOrgUsers(usersResponse.data);

      // Set current collaborators from trip
      setCollaborators(trip.collaborators || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load users and collaborators');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = orgUsers.find(u => u.id === userId);
    if (!user) return userId;
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
  };

  const getUserEmail = (userId) => {
    const user = orgUsers.find(u => u.id === userId);
    return user?.email || '';
  };

  const isUserCollaborator = (userId) => {
    return collaborators.some(c => c.user_id === userId);
  };

  const getCollaboratorPermission = (userId) => {
    const collab = collaborators.find(c => c.user_id === userId);
    return collab?.permission_level || 'view_only';
  };

  const handleAddCollaborator = async (userId) => {
    try {
      setSaving(true);
      setError('');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.post(
        `${apiBase}/trips/${trip.id}/collaborators`,
        {
          user_id: userId,
          permission_level: 'view_only'
        },
        { headers }
      );

      // Add to local state
      setCollaborators([...collaborators, { user_id: userId, permission_level: 'view_only' }]);
    } catch (error) {
      console.error('Failed to add collaborator:', error);
      setError(error.response?.data?.detail || 'Failed to add collaborator');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePermission = async (userId, newPermission) => {
    try {
      setSaving(true);
      setError('');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.put(
        `${apiBase}/trips/${trip.id}/collaborators/${userId}`,
        { permission_level: newPermission },
        { headers }
      );

      // Update local state
      setCollaborators(
        collaborators.map(c =>
          c.user_id === userId ? { ...c, permission_level: newPermission } : c
        )
      );
    } catch (error) {
      console.error('Failed to update permission:', error);
      setError(error.response?.data?.detail || 'Failed to update permission');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCollaborator = async (userId) => {
    const userName = getUserName(userId);
    if (!window.confirm(`Remove ${userName} from this trip?`)) return;

    try {
      setSaving(true);
      setError('');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.delete(
        `${apiBase}/trips/${trip.id}/collaborators/${userId}`,
        { headers }
      );

      // Remove from local state
      setCollaborators(collaborators.filter(c => c.user_id !== userId));
    } catch (error) {
      console.error('Failed to remove collaborator:', error);
      setError(error.response?.data?.detail || 'Failed to remove collaborator');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleConfidentiality = async () => {
    try {
      setSaving(true);
      setError('');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const newValue = !isConfidential;
      await axios.patch(
        `${apiBase}/trips/${trip.id}/confidentiality`,
        { is_confidential: newValue },
        { headers }
      );

      setIsConfidential(newValue);
    } catch (error) {
      console.error('Failed to toggle confidentiality:', error);
      setError(error.response?.data?.detail || 'Failed to update confidentiality setting');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (onUpdate) onUpdate();
    onClose();
  };

  const getPermissionLabel = (level) => {
    switch (level) {
      case 'view_only':
        return 'View Only';
      case 'edit_components':
        return 'Edit Components';
      case 'full_admin':
        return 'Full Admin';
      default:
        return level;
    }
  };

  const creatorName = getUserName(trip.created_by);
  const isCreator = currentUser?.user_id === trip.created_by;
  const isOrgAdmin = currentUser?.is_organization_admin;
  const canManage = isCreator || isOrgAdmin;

  // Users that can be added (not already collaborators and not the creator)
  const availableUsers = orgUsers.filter(
    user => user.id !== trip.created_by && !isUserCollaborator(user.id)
  );

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-ui-surface">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-ui-ink">Manage Trip Permissions</h3>
              <p className="text-sm text-ui-ink-3">{trip.title}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-ui-muted hover:text-ui-ink-3 text-xl"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-primary mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Trip Creator */}
              <div className="bg-ui-violet-bg border border-ui-violet-line rounded-md p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-ui-ink">Trip Creator</div>
                    <div className="text-sm text-ui-ink-3">{creatorName}</div>
                    <div className="text-xs text-ui-muted">{getUserEmail(trip.created_by)}</div>
                  </div>
                  <span className="px-3 py-1 bg-ui-violet-bg text-ui-violet-fg rounded-full text-xs font-medium">
                    Full Admin
                  </span>
                </div>
              </div>

              {/* Confidentiality Toggle */}
              {canManage && (
                <div className="bg-ui-amber-bg border border-ui-amber-line rounded-md p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-ui-ink">Trip Confidentiality</div>
                      <div className="text-xs text-ui-ink-3 mt-1">
                        {isConfidential
                          ? 'This trip is confidential. Only the creator, explicit collaborators, supervisors, and org admins can access it.'
                          : 'This trip is shared with team members. Toggle to make it confidential.'}
                      </div>
                    </div>
                    <button
                      onClick={handleToggleConfidentiality}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ui-amber-solid focus:ring-offset-2 disabled:opacity-50 ${
                        isConfidential ? 'bg-ui-amber-strong' : 'bg-ui-line'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-ui-surface shadow ring-0 transition duration-200 ease-in-out ${
                          isConfidential ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  {isConfidential && (
                    <div className="mt-2 text-xs text-ui-amber-fg bg-ui-amber-bg rounded px-2 py-1">
                      Team members will not be able to see this trip unless added as explicit collaborators.
                    </div>
                  )}
                </div>
              )}

              {/* Current Collaborators */}
              <div>
                <h4 className="text-sm font-medium text-ui-ink mb-3">
                  Current Collaborators ({collaborators.length})
                </h4>
                {collaborators.length === 0 ? (
                  <div className="text-sm text-ui-muted text-center py-4 border border-ui-line rounded-md">
                    No collaborators added yet
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-ui-line rounded-md p-2">
                    {collaborators.map((collab) => (
                      <div
                        key={collab.user_id}
                        className="flex items-center justify-between p-3 bg-ui-sunk rounded-md hover:bg-ui-hover"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-ui-ink">
                            {getUserName(collab.user_id)}
                          </div>
                          <div className="text-xs text-ui-muted">
                            {getUserEmail(collab.user_id)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={collab.permission_level}
                            onChange={(e) => handleUpdatePermission(collab.user_id, e.target.value)}
                            disabled={!canManage || saving}
                            className="text-xs border border-ui-control rounded px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="view_only">View Only</option>
                            <option value="edit_components">Edit Components</option>
                            <option value="full_admin">Full Admin</option>
                          </select>
                          {canManage && (
                            <button
                              onClick={() => handleRemoveCollaborator(collab.user_id)}
                              disabled={saving}
                              className="px-2 py-1 text-xs bg-ui-red-bg text-ui-red-fg hover:bg-ui-red-line rounded disabled:opacity-50"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Collaborators */}
              {canManage && availableUsers.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-ui-ink mb-3">
                    Add Collaborators
                  </h4>
                  <div className="max-h-48 overflow-y-auto border border-ui-line rounded-md p-2">
                    {availableUsers.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-2 hover:bg-ui-hover rounded"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-ui-ink-2">
                            {user.first_name} {user.last_name}
                            {user.is_organization_admin && (
                              <span className="ml-2 text-xs bg-ui-violet-bg text-ui-violet-fg px-2 py-0.5 rounded-full">
                                Org Admin
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-ui-muted">{user.email}</div>
                        </div>
                        <button
                          onClick={() => handleAddCollaborator(user.id)}
                          disabled={saving}
                          className="px-3 py-1 text-xs bg-ui-primary text-ui-on-primary hover:bg-ui-primary-hover rounded disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Permission Info */}
              <div className="bg-ui-blue-bg border border-ui-blue-line rounded-md p-3">
                <div className="text-xs text-ui-blue-fg">
                  <div className="font-medium mb-1">Permission Levels:</div>
                  <ul className="space-y-1 ml-4">
                    <li><strong>View Only:</strong> Can view trip details but cannot edit</li>
                    <li><strong>Edit Components:</strong> Can add/edit/delete trip components</li>
                    <li><strong>Full Admin:</strong> Can edit everything and manage collaborators</li>
                  </ul>
                  {isOrgAdmin && !isCreator && (
                    <div className="mt-2 text-ui-violet-fg font-medium">
                      You can manage permissions as an Organization Admin
                    </div>
                  )}
                  {!canManage && (
                    <div className="mt-2 text-ui-amber-fg font-medium">
                      Only the trip creator or organization admins can manage collaborators
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-3 text-sm text-ui-red-fg">
                  {error}
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t border-ui-line">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-ui-hover hover:bg-ui-line text-ui-ink-2 rounded-md"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTripPermissionsModal;
